// #region Imports
// #endregion
export default class MetadataFactory {
    // #region Vars
    _appHost;
    _cache = new Map();
    _entityCache = new Map();
    // #endregion
    // #region Constructor
    constructor(appHost) {
        // #region Store
        this._appHost = appHost;
        // #endregion
    }
    // #endregion
    // #region common
    getKeyName(object) {
        return `${object.domain}_${object.store}_${object.type}__${object.name}`.toLowerCase();
    }
    // #endregion
    // #region Core
    has(object) {
        return this._cache.has(this.getKeyName(object));
    }
    async get(object, load = true) {
        // #region Load
        if (this.has(object)) {
            return this._cache.get(this.getKeyName(object));
        }
        else if (!load) {
            return null;
        }
        // #endregion
        return this.load(object, true);
    }
    async getData(object, load = true) {
        return (await this.get(object, load))?.data ?? {};
    }
    async getLayoutForEntity(domainName, entityName, viewName, load = true, reload = false) {
        // #region Log
        utils.logTrace("MetadataFactory[getLayoutForEntity()]", { domainName: domainName, entityName: entityName, viewName: viewName, load: load, reload: reload });
        // #endregion
        // #region Entity
        let tEntity = this.getEntityName(entityName);
        {
            if (!tEntity) {
                // #region Log
                utils.logDebug("getLayoutForEntity => Entity not found", { domainName: domainName, entityName: entityName, viewName: viewName, load: load, reload: reload });
                // #endregion
                return null;
            }
        }
        // #endregion
        // #region Optimize
        let tViewName = viewName;
        {
            if (!tViewName) {
                tViewName = "list";
            }
        }
        // #endregion
        // #region Url
        let tUrl = `${domainName}/api/system.layouts/${tEntity.namespace}/${tEntity.name}?view=${viewName}&context=web`;
        // #endregion
        // #region Cache
        if (reload && this._entityCache.has(tUrl)) {
            let tKey = this._entityCache.get(tUrl);
            {
                if (this.has(tKey)) {
                    this._cache.delete(this.getKeyName(tKey));
                }
            }
            this._entityCache.delete(tUrl);
        }
        if (this._entityCache.has(tUrl)) {
            let tKey = this._entityCache.get(tUrl);
            {
                if (this.has(tKey)) {
                    let t1 = this._cache.get(this.getKeyName(tKey));
                    {
                        if (t1 === undefined) {
                            return null;
                        }
                        else {
                            return t1;
                        }
                    }
                }
            }
        }
        else if (!load) {
            return null;
        }
        // #endregion
        // #region Reload
        let tUrl2 = tUrl;
        {
            if (reload) {
                tUrl2 = tUrl2 + '&nocache=1';
            }
        }
        // #endregion
        // #region Load
        let tResult = await this._appHost.api.get(tUrl2);
        {
            if (!tResult.data) {
                return null;
            }
        }
        let tRecord = {
            domain: domainName,
            store: tResult.data.objectStore ?? tResult.data.ObjectStore,
            type: tResult.data.objectType ?? tResult.data.ObjectType,
            name: tResult.data.objectID ?? tResult.data.ObjectID,
            data: {},
            content: {}
        };
        if (tResult.data?.ClientBlob) {
            tRecord.content = utils.decodeBase64(tResult.data.ClientBlob.substring(7));
            tRecord.data = JSON.parse(tRecord.content);
        }
        else if (tResult.data?.clientBlob) {
            tRecord.content = utils.decodeBase64(tResult.data.clientBlob.substring(7));
            tRecord.data = JSON.parse(tRecord.content);
        }
        else {
            tRecord.content = tResult.data?.content;
            tRecord.data = tResult.data?.data;
        }
        //Set to Cache
        {
            this._entityCache.set(tUrl, tRecord);
            this._cache.set(this.getKeyName(tRecord), tRecord);
        }
        // #endregion
        return tRecord;
    }
    async load(object, placeToCache = true) {
        // #region Args
        let tRecord = {
            domain: object.domain,
            store: object.store,
            type: object.type,
            name: object.name,
            data: {},
            content: {}
        };
        // #endregion
        // #region Load
        let tResult = await this._appHost.api.get(`${(object?.domain ?? 'erp')}/api/System.Metadata/Object('${object.store}:${object.type}:${object.name}')?Flags=1`);
        {
            if (tResult.data) {
                //Do nothing
            }
            if (tResult.data?.ClientBlob) {
                tRecord.content = utils.decodeBase64(tResult.data.ClientBlob.substring(7));
                tRecord.data = JSON.parse(tRecord.content);
            }
            else if (tResult.data?.clientBlob) {
                tRecord.content = utils.decodeBase64(tResult.data.clientBlob.substring(7));
                tRecord.data = JSON.parse(tRecord.content);
            }
        }
        // #endregion
        // #region Cache
        if (placeToCache) {
            this._cache.set(this.getKeyName(tRecord), tRecord);
        }
        // #endregion
        return tRecord;
    }
    async update(object, data) {
        let tData = {
            ClientBlob: "{bytes}" + utils.encodeBase64(data)
        };
        //Remove
        await this.remove(object);
        //Run
        await this._appHost.api.put(`${(object?.domain ?? 'erp')}/api/System.Metadata/Object('${object.store}:${object.type}:${object.name}')/Content`, tData);
    }
    async remove(object) {
        let tKey = this.getKeyName(object);
        if (!this.has(object)) {
            return;
        }
        else {
            this._cache.delete(tKey);
        }
    }
    // #endregion
    // #region Helper
    getEntityName(entityName) {
        if (utils.isObject(entityName)) {
            return entityName;
        }
        let tEntityName = entityName;
        {
            if (!tEntityName) {
                return null;
            }
        }
        let tNamespace = '';
        {
            if (tEntityName.indexOf('/') > 0) {
                tNamespace = tEntityName.split('/')[0];
            }
            else if (tEntityName.indexOf('.') > 0) {
                tNamespace = tEntityName.substring(0, tEntityName.lastIndexOf('.'));
            }
            else {
                return null;
            }
        }
        let tName = '';
        {
            if (tEntityName.indexOf('/') > 0) {
                tName = tEntityName.split('/').splice(1).join('/');
            }
            else if (tEntityName.indexOf('.') > 0) {
                let tStart = tEntityName.lastIndexOf('.');
                tName = tEntityName.substring(tStart + 1);
            }
            else {
                return null;
            }
        }
        return { namespace: tNamespace, name: tName };
    }
}
